/**
 * "Go live now": a live made in one step. The title is the only thing to type. The link comes back at once, to
 * share or invite people to before the camera is on, and the studio is one press away.
 */
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import type { QuickLiveResponse } from "../../shared/types";
import { ApiError, api } from "../lib/api";
import { homeZone } from "../lib/zones";
import { InviteSheet } from "./InviteSheet";
import { Button, IconButton } from "./ui/Button";
import { Field } from "./ui/Field";
import { Icon } from "./ui/Icon";
import { Sheet } from "./ui/Sheet";
import { useToast } from "./ui/Toast";
import "../styles/golive.css";

export function GoLiveSheet({
  hostFirst,
  onClose,
  onMade,
}: {
  hostFirst: string;
  onClose: () => void;
  /** The live exists now, so the list behind the sheet has a new card. */
  onMade: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [made, setMade] = useState<QuickLiveResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);

  const make = () => {
    if (busy) return;
    setBusy(true);
    setError("");
    api.webinars
      .quickLive({ ...(title.trim() ? { title: title.trim() } : {}), timezone: homeZone() })
      .then((res) => {
        setMade(res);
        onMade();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "We couldn't make the link. Try again."))
      .finally(() => setBusy(false));
  };

  const copy = () => {
    if (!made) return;
    navigator.clipboard.writeText(made.url).then(
      () => toast("Link copied"),
      () => toast("We couldn't copy the link"),
    );
  };

  if (inviting && made) {
    return (
      <InviteSheet
        webinar={{ id: made.webinar_id, title: made.title, whenLong: "Starting now", length: "", link: made.url }}
        onClose={() => setInviting(false)}
        onSent={(send) => toast(`Sending ${send.total.toLocaleString("en-US")} ${send.total === 1 ? "invite" : "invites"}`)}
        onToast={toast}
      />
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Go live now"
      footer={
        made ? (
          <>
            <Button icon="userplus" onClick={() => setInviting(true)}>
              Invite people
            </Button>
            <Button icon="copy" onClick={copy}>
              Copy link
            </Button>
            <Button variant="p" icon="live" onClick={() => navigate(`/rooms/${made.webinar_id}/studio`)}>
              Open the studio
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="p" icon="link" disabled={busy} onClick={make}>
              {busy ? "Getting the link…" : "Get the link"}
            </Button>
          </>
        )
      }
    >
      {made ? (
        <>
          <div className="okcard" style={{ marginTop: 0 }}>
            <div className="big">
              <Icon name="live" />
            </div>
            <div>
              <div className="b8">“<bdi>{made.title}</bdi>” is ready</div>
            </div>
          </div>
          <div className="field mt16">
            <div className="lbl">Your link</div>
            <div className="copyv">
              <span>{made.url}</span>
              <IconButton icon="copy" label="Copy link" small onClick={copy} />
            </div>
          </div>
        </>
      ) : (
        <Field label="What’s it about?" htmlFor="glq" error={error || undefined}>
          <input dir="auto"
            id="glq"
            className="inp lg"
            value={title}
            placeholder={hostFirst ? `Live with ${hostFirst}` : "Open house Q&A"}
            maxLength={120}
            autoComplete="off"
            enterKeyHint="go"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") make();
            }}
          />
        </Field>
      )}
    </Sheet>
  );
}
